import React, { useState, useEffect } from 'react'
import dashBoardService from '../Services/dashBoardService'

import CustomSelect from '../../components/common/CustomSelect'
import { showErrorToast } from '../../components/Constant/Notification'
import { formatToIndianDate } from '../../utils/dateFormatter'
import UserService from '../../GlobalSetupComponents/Services/UserService'
import { salesDepartmentLabel } from '../../GlobalSetupComponents/Enum/DepartmentEnum'

const TravelAnalysis = () => {
  const [chartData, setChartData] = useState([])
  const [employees, setEmployees] = useState([])
  const [dateRanges, setDateRanges] = useState([])
  const [selectedEmployee, setSelectedEmployee] = useState({ value: '', label: 'All Employees' })
  const [selectedDateRange, setSelectedDateRange] = useState('')
  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 6))
      .toISOString()
      .split('T')[0]
  ) 
  
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Calculate end date (7 days after start date)
  const getEndDate = (startDateString) => {
    const start = new Date(startDateString)
    const end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000) // Add 6 days to make it 7 days total
    return end.toISOString().split('T')[0]
  }

  const calculateHeight = (value, maxValue, chartHeight = 320) => {
    if (maxValue === 0) return 4
    return (value / maxValue) * chartHeight
  }
  

  const processApiData = (data) => {
    if (!data || !data.data) return []
  
    const travelData = data.data.map(item => ({
      date: item.date,
      value: parseFloat(item.km),
      km: item.km 
    }))
  
    const maxValue = Math.max(...travelData.map(item => item.value), 0)
  
    return travelData.map(item => ({
      date: item.date,
      value: item.value,
      km: item.km, 
      height: calculateHeight(item.value, maxValue),
      isZero: item.value === 0
    }))
  }

 
  const validateDateRange = (from, to) => {
    if (!from || !to) return { isValid: false, message: 'Please select start date' }
    
    const fromDateObj = new Date(from)
    const toDateObj = new Date(to)
    
    if (fromDateObj > toDateObj) {
      return { isValid: false, message: 'Invalid date range' }
    }
    
    return { isValid: true, message: '' }
  }


  const formatDateForApi = (dateString) => {
    const date = new Date(dateString)
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const year = date.getFullYear()
    return `${month}-${day}-${year}`
  }
  
  const fetchEmployeeList = async () => {
    try {
      const response = await UserService.getDepartmentWithTeamMembers({
        departmentNames: [salesDepartmentLabel],
        teamId : null
      })
      if (response && response.data) {
        setEmployees(response.data.users || [])
      }
    } catch (error) {
      console.error("Error fetching employee list:", error)
    }
  } 

  useEffect(() => {
    fetchEmployeeList()
  }, [])

  

  // Fetch data from API
  const fetchTravelData = async (employee = '', startDateString = '') => {
    if(!startDateString) {
      return
    }

    const endDateString = getEndDate(startDateString)
    const validation = validateDateRange(startDateString, endDateString)
    if (!validation.isValid) {
      return
    }

    setLoading(true)
    
    try {
      const mockApiResponse = await dashBoardService.getTravelGraphData({
        fromDate: formatDateForApi(startDateString),
        toDate: formatDateForApi(endDateString),
        userUid: employee?.value === 'all' ? '' : employee.value,
      })
      
      const data = mockApiResponse.data || {}
      console.log('Fetched travel data:', data)
      
      const processedData = processApiData(data)
      setChartData(processedData)
      
      if (data.employees) setEmployees(data.employees)
      if (data.dateRanges) setDateRanges(data.dateRanges)
      
    } catch (err) {
      setError(err.message)
      console.error('Error fetching travel data:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (startDate) {
      fetchTravelData(selectedEmployee, startDate)
    }
  }, [startDate, selectedEmployee])
  
  const handleStartDateChange = (e) => {
    const start = e.target.value
    setStartDate(start)
  }

  const handleEmployeeChange = (option) => {
    setSelectedEmployee(option)
  }
  
  const getYAxisLabels = () => {
    if (chartData.length === 0) return ['']
    
    const maxValue = Math.max(...chartData.map(item => item.value))
    if (maxValue === 0) return ['0 KM', '0 KM', '0 KM', '0 KM', '0 KM', '0 KM', '0 KM']
    
    const step = Math.ceil(maxValue / 6)
    const labels = []
    
    for (let i = 6; i >= 0; i--) {
      labels.push(`${step * i} KM`)
    }
    
    return labels
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
        <h2 className="text-xl font-semibold text-gray-800 mb-8">Travel Analysis</h2>
        <div className="text-red-500 text-center py-8">
          Error: {error}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
      <h2 className="text-base font-semibold text-gray-800 mb-8">
      Travel Analysis for 7 days (from {formatToIndianDate(startDate)} to {formatToIndianDate(getEndDate(startDate))})

      </h2>
      
      <div className="mb-8">
        <div className="flex justify-between items-center mb-8">
          <div className="text-[10px] font-medium text-gray-700">
            {/* Travel Analysis for 7 days (from {formatToIndianDate(startDate)} to {formatToIndianDate(getEndDate(startDate))}) */}
          </div>
          <div className="flex gap-4 items-center">
            <CustomSelect
              isClearable={false}
              placeholder="Select Employee"
              options={[{ value: 'all', label: 'All Employees' }, ...employees]}
              onChange={handleEmployeeChange}
              value={selectedEmployee}
            />
            
            <div className="flex gap-2 items-center">
             
              <input
                type="date"
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={startDate}
                onChange={handleStartDateChange}
                disabled={loading}
              />
            </div>
          </div>
        </div>
        
        {/* Chart */}
        <div className="flex h-80 relative">
          {/* Y-axis with grid lines */}
          <div className="flex text-nowrap flex-col justify-between text-xs text-gray-500 w-16 text-right pr-3 relative">
            {getYAxisLabels().map((label, index) => (
              <div key={index} className="relative">
                <span>{label}</span>
              
              </div>
            ))}
            {/* Y-axis line */}
            <div className="absolute right-1 top-0 bottom-0 w-px bg-gray-300"></div>
          </div>
          
          {/* Bars with X-axis */}
          <div className="flex-1 flex items-end justify-between px-4 relative">
            {/* X-axis line */}
                     {loading ? (
              <div className="w-full flex justify-center items-center">
                <div className="animate-spin rounded-full h-14 w-14 border-b-2 border-customGreen"></div>
              </div>
            ) : chartData.length > 0 ? (
              chartData.map((item, index) => (
                <div key={index} className="flex flex-col items-center group relative">
                  {/* Vertical grid line */}
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-px bg-gray-200 h-80 -z-10"></div>
                  
                  <div 
                    className={`w-[130px] bg-gradient-to-t from-blue-100 to-blue-400 rounded-t-[20px] mb-2 transition-all duration-300 group-hover:scale-105 cursor-pointer relative`}
                    style={{ height: `${item.height}px` }}
                    title={`${item.km} KM`}
                  >
                    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 
                                    bg-gray-800 text-white text-xs px-2 py-1 rounded 
                                    opacity-0 group-hover:opacity-100 transition-opacity 
                                    duration-300 whitespace-nowrap">
                      {item.km} KM
                    </div>
                  </div>

                  <span className="text-xs text-gray-500 ">
                    {item.date}
                  </span>
             
                </div>
              ))
            ) : (
              <div className="w-full text-center text-gray-500 py-8">
                {startDate ? 'No data available for selected date range' : 'Please select start date to view data'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default TravelAnalysis